import fm from 'front-matter';
import { CATEGORIES, CATEGORY_MAP } from './categories';

const modules = import.meta.glob('./posts/**/*.mdx', { query: '?raw', import: 'default', eager: true });

const requiredFields = ['title', 'date', 'category', 'tags', 'summary', 'featured'];

const normalizeDate = (dateValue) => {
  const date = new Date(dateValue);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeStatus = (statusValue) => {
  const value = String(statusValue || 'published').trim().toLowerCase();
  return value === 'disabled' ? 'disabled' : 'published';
};

const buildSlugFromPath = (filePath) => {
  const parts = filePath.split('/');
  const fileName = parts[parts.length - 1];
  return fileName.replace(/\.mdx$/, '');
};

const resolvePostFrontmatterCover = (cover) => {
  const value = String(cover || '').trim();
  return value.length > 0 ? value : null;
};

const extractHeadings = (content) => {
  const headingRegex = /^(##|###)\s+(.*)$/gm;
  const headings = [];
  let match;
  while ((match = headingRegex.exec(content))) {
    headings.push(match[2].trim());
  }
  return headings;
};

const normalizePost = (filePath, raw) => {
  const { attributes, body } = fm(raw);
  const data = attributes || {};
  const content = body || '';
  for (const field of requiredFields) {
    if (typeof data[field] === 'undefined') {
      throw new Error(`Frontmatter field "${field}" is required in ${filePath}`);
    }
  }

  if (!CATEGORY_MAP[data.category]) {
    throw new Error(`Unknown category slug "${data.category}" in ${filePath}`);
  }

  const slug = buildSlugFromPath(filePath);
  const dateObject = normalizeDate(data.date);
  if (!dateObject) {
    throw new Error(`Invalid date format in ${filePath}. Expected YYYY-MM-DD`);
  }

  const tags = Array.isArray(data.tags) ? data.tags.map((tag) => String(tag).toLowerCase()) : [];
  const frontmatterCover = resolvePostFrontmatterCover(data.cover);
  const status = normalizeStatus(data.status);
  const sourcePath = filePath.replace(/^\.\//, 'src/content/');

  return {
    slug,
    title: data.title,
    author: data.author || 'Rédaction LMD',
    date: data.date,
    dateObject,
    category: data.category,
    categoryLabel: CATEGORY_MAP[data.category].label,
    tags,
    summary: data.summary,
    featured: Boolean(data.featured),
    status,
    filePath: sourcePath,
    cover: frontmatterCover,
    image: frontmatterCover,
    excerpt: data.summary,
    readingTime: data.readingTime || '5 min',
    readTime: data.readingTime || '5 min',
    content,
    headings: extractHeadings(content)
  };
};

const allPostsIncludingDisabled = Object.entries(modules)
  .map(([filePath, raw]) => normalizePost(filePath, raw))
  .sort((a, b) => b.dateObject - a.dateObject);

const allPosts = allPostsIncludingDisabled.filter((post) => post.status !== 'disabled');

export const getAllPosts = () => allPosts;
export const getAllPostsIncludingDisabled = () => allPostsIncludingDisabled;

export const getPostBySlug = (slug) => allPosts.find((post) => post.slug === slug) || null;

export const getPostsByCategory = (slug) => allPosts.filter((post) => post.category === slug);

export const getPostsByTag = (tag) => allPosts.filter((post) => post.tags.includes(String(tag).toLowerCase()));

export const getFeaturedPosts = () => allPosts.filter((post) => post.featured);

export const getLatestPosts = (limit = 6) => allPosts.slice(0, limit);

export const getRelatedPosts = (post, limit = 4) => {
  const score = (candidate) => {
    const sharedTags = candidate.tags.filter((tag) => post.tags.includes(tag)).length;
    const sameCategoryBonus = candidate.category === post.category ? 2 : 0;
    return sharedTags + sameCategoryBonus;
  };

  return allPosts
    .filter((candidate) => candidate.slug !== post.slug)
    .map((candidate) => ({ candidate, relevance: score(candidate) }))
    .sort((a, b) => b.relevance - a.relevance || b.candidate.dateObject - a.candidate.dateObject)
    .slice(0, limit)
    .map((item) => item.candidate);
};

export const getPrevNextPosts = (slug) => {
  const index = allPosts.findIndex((post) => post.slug === slug);
  if (index === -1) return { prev: null, next: null };
  return {
    prev: allPosts[index + 1] || null,
    next: allPosts[index - 1] || null
  };
};

export const getAllTags = () =>
  [...new Set(allPosts.flatMap((post) => post.tags))]
    .sort((a, b) => a.localeCompare(b, 'fr'));

export const getCategoryList = () => CATEGORIES;
